import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useEndpoints } from "../../api/ApiContext";
import type {
  CreatePromotionRequest,
  GrantPromoCreditResponse,
  PoolFundingResponse,
  Promotion,
  PromotionCommandResponse,
  PromoLiabilityReport,
  PromoPool,
  PromoPoolFunding,
} from "../../api/staffTypes";
import { useStaffSession } from "../../auth/guards";
import { Banner, Button, Card, Checkbox, Dialog, EmptyState, Input, PageHeader, QueryView, RadioGroup, Select, StatusPill, Table, Textarea, type Tone } from "../../components/ui";
import { validateAmountInput } from "../../lib/money";
import {
  Amount,
  AwaitingSecondBanner,
  isUuid,
  Mono,
  MoneyConfirmDialog,
  reasonError,
  shortId,
  StaffRejection,
  StepUpNote,
  useStaffCommand,
  when,
} from "./staffCommon";

// Promotional credit (migration 0075, promotions.go). Promotions are
// staff-managed offers ("deposit $50, get $100 credit"); credit is granted
// when a qualifying deposit is credited, or by a staff grant here. Credit
// is not money anyone deposited: it is spent after cash, never refunded,
// expires. Publishers' share of promo-funded impressions is promotional
// earnings: a VADS liability paid only when two reviewers fund the
// promotional payout pool, which converts them to ordinary earnings pro
// rata across publishers, oldest first within one.

const CURRENCY = "USD";

const promoKeys = {
  list: ["staff", "promotions", "list"] as const,
  liability: ["staff", "promotions", "liability"] as const,
  pool: ["staff", "promotions", "pool"] as const,
};

const STATE_TONE: Record<Promotion["state"], Tone> = { live: "success", scheduled: "info", lapsed: "neutral", ended: "neutral" };

function offer(p: Promotion): string {
  const credit = p.credit_kind === "fixed" ? `${trim(p.fixed_amount)} credit` : `${Math.round(Number(p.match_rate) * 100)}% match up to ${trim(p.match_cap)}`;
  if (!p.applies_to_deposits) return `${credit} (staff grants only)`;
  return Number(p.min_deposit_amount) > 0 ? `Deposit ${trim(p.min_deposit_amount)}+, get ${credit}` : `Any deposit: ${credit}`;
}

function trim(v: string | null | undefined): string {
  if (!v) return "—";
  const [i, f = ""] = v.split(".");
  const cents = f.replace(/0+$/, "");
  return cents ? `${i}.${cents.padEnd(2, "0")}` : i!;
}

export function PromotionsPage() {
  return (
    <div className="stack-lg">
      <PageHeader
        title="Promotions"
        description="Promotional credit offers, the outstanding liability on both sides, and the dual-controlled promotional payout pool."
      />
      <LiabilityCard />
      <PromotionsCard />
      <CreatePromotionCard />
      <GrantCard />
      <PoolCard />
    </div>
  );
}

function LiabilityCard() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: promoKeys.liability, queryFn: () => ep.staff.promotions.liability() });
  return (
    <Card title="Outstanding promotional liability" subtitle="Non-cash obligations VADS has issued: advertisers' promo credit and publishers' promotional earnings.">
      <QueryView query={q} loadingLabel="Loading the liability report">
        {(r: PromoLiabilityReport) =>
          r.currencies.length ? (
            <Table
              caption="Promotional liability by currency"
              rows={r.currencies}
              rowKey={(c) => c.currency}
              columns={[
                { key: "cur", header: "Currency", cell: (c) => c.currency },
                { key: "credit", header: "Promo credit outstanding", numeric: true, cell: (c) => <Amount value={c.promo_credit_outstanding} currency={c.currency} /> },
                { key: "flight", header: "Credit in live ads", numeric: true, cell: (c) => <Amount value={c.promo_credit_in_flight} currency={c.currency} /> },
                { key: "earn", header: "Promotional earnings outstanding", numeric: true, cell: (c) => <Amount value={c.promotional_earnings_outstanding} currency={c.currency} /> },
                { key: "elig", header: "Convertible now", numeric: true, cell: (c) => <Amount value={c.promotional_earnings_eligible_now} currency={c.currency} /> },
                { key: "pool", header: "Pool balance", numeric: true, cell: (c) => <Amount value={c.pool_balance} currency={c.currency} /> },
                { key: "exp", header: "Marketing expense to date", numeric: true, cell: (c) => <Amount value={c.marketing_expense_to_date} currency={c.currency} /> },
              ]}
            />
          ) : (
            <EmptyState title="Nothing outstanding">No promotional credit or promotional earnings exist yet.</EmptyState>
          )
        }
      </QueryView>
    </Card>
  );
}

function PromotionsCard() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: promoKeys.list, queryFn: () => ep.staff.promotions.list() });
  const [ending, setEnding] = useState<Promotion | null>(null);
  return (
    <Card title="Promotions">
      <QueryView query={q} loadingLabel="Loading promotions">
        {(d) => (
          <Table
            caption="Promotions, newest first"
            captionHidden
            rows={d.promotions}
            rowKey={(p) => p.promotion_id}
            empty={<EmptyState title="No promotions yet">Create one below.</EmptyState>}
            columns={[
              { key: "name", header: "Promotion", cell: (p) => <span>{p.name}{p.code ? <> · <Mono>{p.code}</Mono></> : null}</span> },
              { key: "offer", header: "Offer", cell: (p) => offer(p) },
              { key: "rules", header: "Rules", cell: (p) => `${p.per_advertiser_limit}× per advertiser · expires ${p.credit_expiry_days} days after grant` },
              { key: "window", header: "Valid", cell: (p) => `${when(p.valid_from)} → ${p.valid_until ? when(p.valid_until) : "open-ended"}` },
              { key: "state", header: "State", cell: (p) => <StatusPill tone={STATE_TONE[p.state]}>{p.state}</StatusPill> },
              { key: "granted", header: "Granted", numeric: true, cell: (p) => <Amount value={p.granted_total ?? "0"} currency={p.currency} /> },
              { key: "out", header: "Unused", numeric: true, cell: (p) => <Amount value={p.outstanding_total ?? "0"} currency={p.currency} /> },
              {
                key: "act",
                header: "Action",
                cell: (p) =>
                  p.state === "ended" ? (
                    "—"
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => setEnding(p)}>
                      End
                    </Button>
                  ),
              },
            ]}
          />
        )}
      </QueryView>
      {ending ? <EndDialog p={ending} onClose={() => setEnding(null)} /> : null}
    </Card>
  );
}

function EndDialog({ p, onClose }: { p: Promotion; onClose: () => void }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const cmd = useStaffCommand(
    (req: { reason: string }, opts) => ep.staff.promotions.end(p.promotion_id, req.reason, opts),
    () => {
      void qc.invalidateQueries({ queryKey: ["staff", "promotions"] });
      onClose();
    },
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={`End “${p.name}”`}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={cmd.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={cmd.isPending}
            onClick={() => {
              const e = reasonError(reason);
              setErr(e);
              if (!e) cmd.run({ reason: reason.trim() });
            }}
          >
            End promotion
          </Button>
        </>
      }
    >
      <div className="stack-sm">
        <p className="small">No new deposit earns it and its code stops redeeming. Credit already granted stays valid until it expires.</p>
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} error={err ?? undefined} />
        <StepUpNote required />
        {cmd.error ? <StaffRejection error={cmd.error} /> : null}
      </div>
    </Dialog>
  );
}

function CreatePromotionCard() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: "",
    description: "",
    code: "",
    kind: "fixed" as "fixed" | "match",
    fixed: "",
    rate: "",
    cap: "",
    min: "",
    limit: "1",
    days: "90",
    deposits: true,
    until: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<PromotionCommandResponse | null>(null);
  const cmd = useStaffCommand(
    (req: CreatePromotionRequest, opts) => ep.staff.promotions.create(req, opts),
    (res) => {
      setCreated(res);
      void qc.invalidateQueries({ queryKey: ["staff", "promotions"] });
    },
  );
  const set = (k: keyof typeof form) => (v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  function submit(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "Give the promotion a name.";
    const money = (k: "fixed" | "cap" | "min", v: string, required: boolean) => {
      if (!v.trim()) {
        if (required) errs[k] = "Required.";
        return undefined;
      }
      const r = validateAmountInput(v, CURRENCY);
      if (!r.ok) errs[k] = "Enter an amount with up to 2 decimal places.";
      return r.ok ? r.value : undefined;
    };
    const fixed = form.kind === "fixed" ? money("fixed", form.fixed, true) : undefined;
    const cap = form.kind === "match" ? money("cap", form.cap, true) : undefined;
    const min = money("min", form.min, false);
    if (form.kind === "match" && !/^\d+(\.\d{1,6})?$/.test(form.rate.trim())) errs.rate = "A rate like 1 (100%) or 0.5 (50%).";
    if (!/^\d+$/.test(form.limit) || Number(form.limit) < 1) errs.limit = "At least 1.";
    if (!/^\d+$/.test(form.days) || Number(form.days) < 1) errs.days = "At least 1 day.";
    if (form.code.trim() && !/^[A-Za-z0-9][A-Za-z0-9_-]{2,39}$/.test(form.code.trim())) errs.code = "3–40 letters, digits, - or _.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const req: CreatePromotionRequest = {
      name: form.name.trim(),
      description: form.description.trim(),
      credit_kind: form.kind,
      applies_to_deposits: form.deposits,
      per_advertiser_limit: Number(form.limit),
      credit_expiry_days: Number(form.days),
    };
    if (form.code.trim()) req.code = form.code.trim().toUpperCase();
    if (fixed) req.fixed_amount = fixed;
    if (form.kind === "match") {
      req.match_rate = form.rate.trim();
      req.match_cap = cap;
    }
    if (min) req.min_deposit_amount = min;
    if (form.until) req.valid_until = new Date(`${form.until}T00:00:00Z`).toISOString();
    cmd.run(req);
  }

  return (
    <Card title="Create a promotion" subtitle="Credit is granted when a qualifying deposit is credited (never at checkout), or by a staff grant.">
      <form className="stack" onSubmit={submit} noValidate>
        <div className="grid-auto">
          <Input label="Name" value={form.name} onChange={(e) => set("name")(e.target.value)} error={errors.name} />
          <Input label="Code" optional mono value={form.code} onChange={(e) => set("code")(e.target.value)} error={errors.code} hint="Advertisers enter it before depositing. Leave empty for an automatic offer." />
        </div>
        <Textarea label="Description" optional value={form.description} onChange={(e) => set("description")(e.target.value)} />
        <RadioGroup
          legend="Credit"
          name="credit_kind"
          value={form.kind}
          onChange={(v) => set("kind")(v)}
          options={[
            { value: "fixed", label: "A fixed amount" },
            { value: "match", label: "A match of the deposit, with a cap" },
          ]}
        />
        <div className="grid-auto">
          {form.kind === "fixed" ? (
            <Input label={`Credit (${CURRENCY})`} inputMode="decimal" value={form.fixed} onChange={(e) => set("fixed")(e.target.value)} error={errors.fixed} />
          ) : (
            <>
              <Input label="Match rate" inputMode="decimal" value={form.rate} onChange={(e) => set("rate")(e.target.value)} error={errors.rate} hint="1 = 100% of the deposit." />
              <Input label={`Cap (${CURRENCY})`} inputMode="decimal" value={form.cap} onChange={(e) => set("cap")(e.target.value)} error={errors.cap} />
            </>
          )}
          <Input label={`Minimum deposit (${CURRENCY})`} optional inputMode="decimal" value={form.min} onChange={(e) => set("min")(e.target.value)} error={errors.min} />
          <Input label="Per advertiser" inputMode="numeric" value={form.limit} onChange={(e) => set("limit")(e.target.value)} error={errors.limit} hint="How many times one advertiser can receive it." />
          <Input label="Credit expires after (days)" inputMode="numeric" value={form.days} onChange={(e) => set("days")(e.target.value)} error={errors.days} />
          <Input label="Valid until" optional type="date" value={form.until} onChange={(e) => set("until")(e.target.value)} />
        </div>
        <Checkbox
          label="Granted on qualifying deposits"
          checked={form.deposits}
          onChange={(e) => set("deposits")(e.target.checked)}
          hint="Unticked: staff grants only."
        />
        <StepUpNote required />
        {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        {created ? (
          <Banner tone="success" live="polite" title={created.replayed ? "Already created" : "Promotion created"}>
            {created.promotion.name}: {offer(created.promotion)}.
          </Banner>
        ) : null}
        <div>
          <Button type="submit" loading={cmd.isPending}>
            Create promotion
          </Button>
        </div>
      </form>
    </Card>
  );
}

function GrantCard() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: promoKeys.list, queryFn: () => ep.staff.promotions.list() });
  const live = (list.data?.promotions ?? []).filter((p) => p.state !== "ended");
  const [promotionId, setPromotionId] = useState("");
  const [advertiserId, setAdvertiserId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<{ amount: string } | null>(null);
  const [done, setDone] = useState<GrantPromoCreditResponse | null>(null);
  const cmd = useStaffCommand(
    (req: { amount?: string }, opts) =>
      ep.staff.promotions.grant(promotionId, { advertiser_id: advertiserId.trim().toLowerCase(), amount: req.amount, reason: reason.trim() }, opts),
    (res) => {
      setDone(res);
      setConfirm(null);
      void qc.invalidateQueries({ queryKey: ["staff", "promotions"] });
    },
  );
  const promo = live.find((p) => p.promotion_id === promotionId);

  function submit(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!promo) errs.promotion = "Choose a promotion.";
    if (!isUuid(advertiserId)) errs.advertiser = "The advertiser's account id (a UUID).";
    let value = promo?.fixed_amount ?? "";
    if (amount.trim()) {
      const r = validateAmountInput(amount, CURRENCY);
      if (!r.ok) errs.amount = "Enter an amount with up to 2 decimal places.";
      else value = r.value;
    } else if (promo?.credit_kind === "match") errs.amount = "A match promotion needs an amount (up to its cap).";
    const re = reasonError(reason);
    if (re) errs.reason = re;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setDone(null);
    setConfirm({ amount: value });
  }

  return (
    <Card title="Grant credit to an advertiser" subtitle="Directly, under a promotion: its per-advertiser limit and credit expiry apply. Recorded on the advertiser's audit log.">
      <form className="stack" onSubmit={submit} noValidate>
        <div className="grid-auto">
          <Select
            label="Promotion"
            value={promotionId}
            placeholder="Choose…"
            onChange={(e) => setPromotionId(e.target.value)}
            error={errors.promotion}
            options={live.map((p) => ({ value: p.promotion_id, label: `${p.name} (${offer(p)})` }))}
          />
          <Input label="Advertiser account id" mono value={advertiserId} onChange={(e) => setAdvertiserId(e.target.value)} error={errors.advertiser} />
          <Input label={`Amount (${CURRENCY})`} optional inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} error={errors.amount} hint="Empty: the promotion's fixed amount." />
        </div>
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} error={errors.reason} />
        <StepUpNote required />
        {done ? (
          <Banner tone="success" live="polite" title={done.replayed ? "Already granted" : "Credit granted"}>
            <Amount value={done.amount} currency={done.currency} /> to {shortId(done.advertiser_id)}, expiring {when(done.expires_at)}.
          </Banner>
        ) : null}
        <div>
          <Button type="submit">Review grant</Button>
        </div>
      </form>
      <MoneyConfirmDialog
        open={!!confirm}
        title="Grant promotional credit"
        amount={confirm?.amount ?? "0"}
        currency={CURRENCY}
        effect={
          <>
            Adds non-refundable promotional credit to advertiser <Mono>{advertiserId.trim()}</Mono>. It is spent after their cash, never
            refunded or paid out, and expires {promo ? `${promo.credit_expiry_days} days` : ""} after today. No cash moves.
          </>
        }
        confirmLabel="Grant credit"
        busy={cmd.isPending}
        error={cmd.error}
        onConfirm={() => cmd.run({ amount: amount.trim() ? confirm?.amount : undefined })}
        onCancel={() => setConfirm(null)}
      />
    </Card>
  );
}

function PoolCard() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const { me } = useStaffSession();
  const q = useQuery({ queryKey: promoKeys.pool, queryFn: () => ep.staff.promotions.pool() });
  const [amount, setAmount] = useState("");
  const [explanation, setExplanation] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<{ amount: string } | null>(null);
  const [approving, setApproving] = useState<PromoPoolFunding | null>(null);
  const [last, setLast] = useState<PoolFundingResponse | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["staff", "promotions"] });
  const propose = useStaffCommand(
    (req: { amount: string; explanation: string }, opts) => ep.staff.promotions.proposeFunding(req, opts),
    (res) => {
      setLast(res);
      setConfirm(null);
      setAmount("");
      setExplanation("");
      refresh();
    },
  );
  const approve = useStaffCommand(
    (req: { id: string; amount: string }, opts) => ep.staff.promotions.approveFunding(req.id, req.amount, opts),
    (res) => {
      setLast(res);
      setApproving(null);
      refresh();
    },
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const r = validateAmountInput(amount, CURRENCY);
    if (!r.ok) errs.amount = "Enter an amount with up to 2 decimal places.";
    const re = reasonError(explanation);
    if (re) errs.explanation = re;
    setErrors(errs);
    if (!r.ok || re) return;
    setLast(null);
    setConfirm({ amount: r.value });
  }

  return (
    <Card
      title="Promotional payout pool"
      subtitle="Two different reviewers: one proposes an amount, a second confirms it. Funding converts publishers' promotional earnings to ordinary earnings — pro rata across publishers, oldest first within each — and the usual payout hold and minimum then apply."
    >
      <QueryView query={q} loadingLabel="Loading the pool">
        {(p: PromoPool) => (
          <div className="stack">
            <dl className="definition-list">
              <dt>Pool balance</dt>
              <dd>
                <Amount value={p.preview.pool_balance} currency={p.preview.currency} />
              </dd>
              <dt>Convertible now</dt>
              <dd>
                <Amount value={p.preview.eligible_total} currency={p.preview.currency} />{" "}
                <span className="xs muted">
                  across {p.preview.eligible_publishers} publisher{p.preview.eligible_publishers === 1 ? "" : "s"} (past the review window, KYC
                  cleared, account open)
                </span>
              </dd>
            </dl>
            <Table
              caption="Pool fundings"
              rows={p.fundings}
              rowKey={(f) => f.funding_id}
              empty={<EmptyState title="No fundings yet">Propose one below.</EmptyState>}
              columns={[
                { key: "at", header: "Proposed", cell: (f) => when(f.proposed_at) },
                { key: "amt", header: "Amount", numeric: true, cell: (f) => <Amount value={f.amount} currency={f.currency} /> },
                { key: "why", header: "Explanation", cell: (f) => f.explanation },
                {
                  key: "st",
                  header: "Status",
                  cell: (f) =>
                    f.status === "executed" ? (
                      <span>
                        <StatusPill tone="success">Executed</StatusPill>{" "}
                        <span className="xs muted">
                          converted <Amount value={f.converted_amount} currency={f.currency} /> for {f.publishers_converted ?? 0} publisher(s)
                        </span>
                      </span>
                    ) : f.status === "expired" ? (
                      <StatusPill tone="neutral">Expired unapproved</StatusPill>
                    ) : (
                      <StatusPill tone="warning">Waiting for a second reviewer</StatusPill>
                    ),
                },
                {
                  key: "act",
                  header: "Action",
                  cell: (f) =>
                    f.status !== "proposed" ? (
                      "—"
                    ) : f.proposed_by === me.user_id ? (
                      <span className="xs muted">Your proposal: another reviewer approves</span>
                    ) : (
                      <Button size="sm" onClick={() => setApproving(f)}>
                        Approve
                      </Button>
                    ),
                },
              ]}
            />
          </div>
        )}
      </QueryView>
      <form className="stack" onSubmit={submit} noValidate style={{ marginTop: "var(--space-4)" }}>
        <div className="grid-auto">
          <Input label={`Amount (${CURRENCY})`} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} error={errors.amount} />
        </div>
        <Textarea label="Explanation" value={explanation} onChange={(e) => setExplanation(e.target.value)} error={errors.explanation} />
        <StepUpNote required />
        {last?.result === "awaiting_second_approval" ? <AwaitingSecondBanner approvals={last.approvals} required={last.approvals_required} /> : null}
        {last?.result === "executed" ? (
          <Banner tone="success" live="polite" title="Pool funded">
            Converted <Amount value={last.funding.converted_amount} currency={last.funding.currency} />; the pool now holds{" "}
            <Amount value={last.funding.pool_balance_after} currency={last.funding.currency} />.
          </Banner>
        ) : null}
        <div>
          <Button type="submit">Propose funding</Button>
        </div>
      </form>
      <MoneyConfirmDialog
        open={!!confirm}
        title="Propose a pool funding"
        amount={confirm?.amount ?? "0"}
        currency={CURRENCY}
        effect="Commits VADS money to paying publishers' promotional earnings (a marketing expense). Nothing moves until a second reviewer approves."
        pendingSecond="This is the proposal."
        confirmLabel="Propose"
        busy={propose.isPending}
        error={propose.error}
        onConfirm={() => confirm && propose.run({ amount: confirm.amount, explanation: explanation.trim() })}
        onCancel={() => setConfirm(null)}
      />
      <MoneyConfirmDialog
        open={!!approving}
        title="Approve the pool funding"
        amount={approving?.amount ?? "0"}
        currency={approving?.currency ?? CURRENCY}
        effect={
          <>
            Funds the promotional payout pool from marketing expense and converts promotional earnings up to the pool balance into
            ordinary, payable earnings. Proposed by {approving ? shortId(approving.proposed_by) : ""}: “{approving?.explanation}”.
          </>
        }
        confirmLabel="Approve and fund"
        busy={approve.isPending}
        error={approve.error}
        onConfirm={() => approving && approve.run({ id: approving.funding_id, amount: approving.amount })}
        onCancel={() => setApproving(null)}
      />
    </Card>
  );
}
